import {
  AdminInput,
  AdminSelect,
  AdminSpinner,
  Button,
  Div,
  H5,
  Img,
  Modal,
  PropsModal,
  ProviderLogoWrapper,
  SimpleLocationEntity,
  SuccessToast,
  connectLocationWithProvider,
  connectProvider,
  handleError,
  isAdmin,
  isDirectProviderConnection,
  isFoodHubProviderConnection,
  isKitchenHubProviderConnection,
  isPartner,
  renderProviderImage,
  renderProviderName,
  useIsIOSSafari,
  useSafeWindowOpen,
  useVDrive,
  reportError,
} from '@vgrubs/components';
import {
  ProviderAndProviderStatusResponse,
  ProviderConnectionType,
} from 'components/src/types/api/location.types';
import { Provider, ProviderConnect } from 'components/src/types/shared.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { handleDisconnect } from './ManageProviders';

const options: {
  name: string;
  value: ProviderConnectionType;
}[] = [
  { name: 'FoodHub', value: ProviderConnectionType.foodhub },
  { name: 'KitchenHub', value: ProviderConnectionType.thirdParty },
];

const renderOptions = (
  <>
    {options.map((r, i) => (
      <option value={r.value} key={i}>
        {r.name}
      </option>
    ))}
  </>
);

export const ProviderConnectModal = ({
  modalIsOpen,
  closeModal,
  setStatuses,
  statuses,
  id,
  provider,
  pathPrefix,
  setProviderConnect,
  getProvidersStatuses,
  setSetupMenuToContinueModal,
  location,
}: PropsModal & {
  modalIsOpen: boolean;
  provider: Provider;
  id: string | number;
  pathPrefix: 'locations' | 'brands';
  setStatuses: (value: ProviderAndProviderStatusResponse) => void;
  statuses: ProviderAndProviderStatusResponse;
  setProviderConnect: (provider: ProviderConnect | null) => void;
  getProvidersStatuses: (silent?: boolean, buttonLoading?: boolean) => void;
  setSetupMenuToContinueModal: Dispatch<SetStateAction<boolean>>;
  location: SimpleLocationEntity | null;
}) => {
  const { setLocationVDrive, setBrandVDrive } = useVDrive();
  const openSafe = useSafeWindowOpen();
  const isIOSSafari = useIsIOSSafari();
  const [loading, setLoading] = useState(false);
  const [storeId, setStoreId] = useState('');
  const [url, setUrl] = useState<{ url: string; isNewTab: boolean } | null>(
    null
  );
  const [foodhubKitchenhub, setFoodhubKitchenhub] =
    useState<ProviderConnectionType>(ProviderConnectionType.foodhub);
  const localhost = true;
  const hasStoreId = isDirectProviderConnection(provider);

  const connectFunction =
    pathPrefix === 'locations' ? connectLocationWithProvider : connectProvider;

  const handleRedirect = (url: string, isNewTab: boolean) => {
    openSafe(url, {
      immediate: true,
      target: isNewTab ? '_blank' : '_self',
    });

    closeModal?.();
    setUrl(null);
  };

  async function handleConnect() {
    try {
      setLoading(true);
      const res = await connectFunction(id, {
        provider,
        storeId: hasStoreId ? storeId : null,
        redirectUrl: `${
          localhost ? process.env.NX_ADMIN_URL : window.location.origin
        }/${pathPrefix}/manage-providers/${id}${
          pathPrefix === 'brands' ? (location?.id ? `/${location.id}` : '') : ''
        }?cp=${provider}`,
        shouldUseFoodhub:
          provider === Provider.localForYou
            ? true
            : isFoodHubProviderConnection(provider)
            ? foodhubKitchenhub === ProviderConnectionType.foodhub
            : undefined,
      });

      if (!hasStoreId) {
        setLoading(false);
        const url = res.data.data;

        if (isKitchenHubProviderConnection(provider)) {
          setProviderConnect({ provider, id, pathPrefix });
          if (isIOSSafari) {
            setUrl({ url, isNewTab: false });
          } else {
            handleRedirect(url, false);
          }
        }

        if (isFoodHubProviderConnection(provider)) {
          if (!url.includes('https://')) {
            closeModal?.();
            setSetupMenuToContinueModal(true);
          } else {
            if (isIOSSafari) {
              setUrl({
                url,
                isNewTab: foodhubKitchenhub === ProviderConnectionType.foodhub,
              });
            } else {
              handleRedirect(
                url,
                foodhubKitchenhub === ProviderConnectionType.foodhub
              );
            }
          }
        }
      } else {
        SuccessToast(res.data.message);
        closeModal?.();
        setStoreId('');
        setFoodhubKitchenhub(ProviderConnectionType.foodhub);
      }

      getProvidersStatuses(true);

      if (pathPrefix === 'locations') {
        setLocationVDrive(null);
      } else {
        setBrandVDrive(null);
      }
    } catch (error) {
      if (
        isKitchenHubProviderConnection(provider) &&
        handleError(error) === `${provider} is already connected.`
      ) {
        handleDisconnect(
          pathPrefix,
          id,
          provider,
          statuses,
          setStatuses,
          setLoading,
          () => handleConnect()
        );
      } else {
        setLoading(false);
        reportError(error);
      }
    } finally {
      setLoading(false);
    }
  }

  const connectionDisabled = provider === Provider.kanekt365;

  return (
    <>
      {loading && <AdminSpinner />}

      <Modal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header="Connect Provider"
      >
        <Div $flex $center>
          <ProviderLogoWrapper>
            <span>
              <Img alt="" src={renderProviderImage(provider)} />
            </span>
          </ProviderLogoWrapper>
        </Div>

        {hasStoreId ? (
          <AdminInput
            $textCenter
            value={storeId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setStoreId(e.target.value)
            }
            label="Enter your Store ID"
          />
        ) : (
          provider !== Provider.kanekt365 && (
            <>
              {!url && (
                <H5 $mb="16">
                  Click on button to connect {renderProviderName(provider)} with
                  our app
                </H5>
              )}
              {url && (
                <H5 $mb="16">You will be redirected to authorization page</H5>
              )}
              {/* <P $mb="24">You will be redirected to authorization page</P> */}
            </>
          )
        )}

        {connectionDisabled && (
          <H5 $mb="16">
            Kanekt 365 handles the connection on their end. It cannot be
            connected from the admin platform.
          </H5>
        )}

        {isFoodHubProviderConnection(provider) &&
          provider !== Provider.localForYou && (
            <>
              <AdminSelect
                label="Choose Ordering Service"
                options={renderOptions}
                value={foodhubKitchenhub}
                onChange={(e) =>
                  setFoodhubKitchenhub(e.target.value as ProviderConnectionType)
                }
                $textCenter
              />
            </>
          )}

        {!url && (
          <Button
            $selected
            $w="100%"
            $mt="16"
            onClick={() => handleConnect()}
            disabled={connectionDisabled}
          >
            Connect
          </Button>
        )}

        {url && (
          <Button
            $selected
            $w="100%"
            $mt="16"
            onClick={() => handleRedirect(url.url, url.isNewTab)}
          >
            Proceed to authorization page
          </Button>
        )}
      </Modal>
    </>
  );
};
